import { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import { Box, Stack, Chip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ArrowForwardIos, Close } from '@mui/icons-material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SegmentedLinearProgress from '../../components/common/SegmentedLinearProgress';
import { useTranslation } from 'react-i18next';

export const statusChipColor = (status) => {
    const key = String(status || '').toLowerCase();
    if (key === 'succeeded') return '#39B620';
    if (key === 'failed') return '#CA3C3D';
    if (key === 'started' || key === 'initiated' || key === 'completed' ) return '#0088F2';
    if (key === 'scheduled' || key === 'pending') return '#F0AA1F';
    return 'default';
};

export const pad2 = (n) => String(n).padStart(2, '0');
export const asDate = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
        // Require an explicit timezone offset (Z or ±HH:MM with valid range).
        // Bare date strings and no-tz datetimes are ambiguous and rejected.
        // The range check (hours 00-23, minutes 00-59) closes the gap in a
        // naive /[+-]\d{2}:\d{2}$/ regex that would accept offsets like +99:99.
        // Switch to Temporal when per-test environment overrides are available via Vite's Vitest:
        // Use "//@vitest-environment node" as a top-of-file override in *.test.jsx.
        // https://tc39.es/proposal-temporal/docs/timezone.html#parsing-timezones
        const validISOOffset = /(Z|[+-]([01]\d|2[0-3]):[0-5]\d)$/;
        if (!validISOOffset.test(value)) {
            console.error(`asDate: "${value}" is not a valid ISO 8601 timestamp with explicit timezone offset (e.g. '2024-01-15T10:00:00Z').`);
            return null;
        }
    }
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d) ? null : d;
};

// Format: hh.mm AM/PM (local time)
export const formatTimeDot = (value) => {
    const d = asDate(value);
    if (!d) return '-';
    let h = d.getHours();
    const m = pad2(d.getMinutes());
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return (
        <>
            <span style={{ fontWeight: 700 }}>{`${pad2(h)}.${m}`}</span> {ampm}
        </>
    );
};

// Format: MM/DD/YY
// Expects a full ISO 8601 UTC timestamp (e.g. '2024-01-15T10:00:00Z') as
// produced by the backend — not a bare date string, which would be ambiguous.
// Pass an IANA timeZone string (e.g. 'America/New_York') to override the
// process-local timezone — useful for tests and future timezone-aware display.
export const formatLocalShortDate = (value, timeZone = undefined) => {
    const d = asDate(value);
    if (!d) return '-';
    if (timeZone !== undefined) {
        // en-US locale gives a consistent MM/DD/YY format regardless of the
        // environment locale. Caller controls which day is displayed.
        return new Intl.DateTimeFormat('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            timeZone,
        }).format(d);
    }
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
};

const IndividualJobComponent = (props) => {
    const { t } = useTranslation();
    const { job, calledFor } = props;
    const total = job?.totalDevices || 0;
    const segments = useMemo(() => {
        return (job?.firmwareUpdateStatus || []).map((s) => ({
            value: (s.count / total) * 100,
            color: statusChipColor(s.status) === 'default' ? 'grey' : statusChipColor(s.status),
            label: `${s.status}: ${s.count}`,
        }));
    }, [job, total]);

    return (
        <Stack spacing={3} className="dlm-job-overview-stack">
            <Paper variant="outlined" sx={{ p: 2 }} className="dlm-job-paper">
                <Box sx={{ display: 'flex', alignItems: 'center' }} className="outer-box">
                    <Box sx={{ width: '10%', pr: 2 }} className="dlm-job-date-time">
                        <Stack
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontFamily: 'Open Sans',
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    letterSpacing: '0px',
                                }}
                            >
                                {formatTimeDot(job?.createdAt)}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontFamily: 'Open Sans',
                                    fontWeight: 400,
                                    fontStyle: 'normal',
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    letterSpacing: '0px',
                                }}
                            >
                                {formatLocalShortDate(job?.createdAt)}
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ width: '80%', pr: 2 }} className="dlm-job-info">
                        <Stack
                            spacing={0}
                            className="dlm-job-userinfo-segment"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                height: 45,
                            }}
                        >
                            <div className="device-site-info-segment" style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: 'Open Sans',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        letterSpacing: '0px',
                                        verticalAlign: 'middle',
                                    }}
                                    className="device-to-version-info"
                                >
                                    {`${job?.deviceToVersionInfo || ''}`}
                                </Typography>
                                <ArrowRightIcon />
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: 'Open Sans',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        letterSpacing: '0px',
                                        verticalAlign: 'middle',
                                    }}
                                    className="site-count-info"
                                >
                                    {`${job?.siteCountInfo || ''}`}
                                </Typography>
                                <ArrowRightIcon />
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: 'Open Sans',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        letterSpacing: '0px',
                                        verticalAlign: 'middle',
                                    }}
                                    className="device-count-info"
                                >
                                    {`${job?.deviceCountInfo || ''}`}
                                </Typography>
                            </div>

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ width: '100%' }}
                                className="user-name-stack"
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: 'Open Sans',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '100%',
                                        letterSpacing: '0px',
                                        verticalAlign: 'middle',
                                    }}
                                    className="user-name"
                                >
                                    By {job?.createdBy}
                                </Typography>
                                {calledFor === 'InProgress' && (
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontFamily: 'Open Sans',
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            lineHeight: '100%',
                                            letterSpacing: '0px',
                                            ml: 'auto',
                                        }}
                                        className="percentage-display"
                                    >
                                        {`${job?.percentage}%`}
                                    </Typography>
                                )}
                                {job?.status === 'Cancelled' && (
                                    <Chip
                                        label={t('translation:pages.dlm.cancelled')}
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        className="cancelled-chip"
                                        sx={{
                                            fontFamily: 'Open Sans',
                                            fontWeight: 700,
                                            fontSize: '10px',
                                            lineHeight: '16px',
                                            letterSpacing: '1px',
                                            textAlign: 'center',
                                            textTransform: 'uppercase',
                                            borderWidth: 2,
                                            borderColor: 'red',
                                            color: 'red',
                                            borderRadius: '2px',
                                            marginLeft: '2%'
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} className="progress-bar">
                            <SegmentedLinearProgress  radius={6} segments={segments} />
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            width: '10%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'end',
                        }}
                        className="dlm-job-icons-control-block"
                    >
                        {calledFor === 'InProgress' && (
                            <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center' }} className="cancel-button">
                                <Tooltip title={t('translation:pages.dlm.cancelUpdates')}>
                                    <IconButton color="error" aria-label="cancel-job">
                                        <Close />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        <Box
                            className="view-details-button"
                            sx={{
                                width: '35%',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <Tooltip title={t('translation:pages.dlm.viewDetails')}>
                                <IconButton color="primary" aria-label="view-details">
                                    <ArrowForwardIos />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
};

export default IndividualJobComponent;
